import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Input, Modal, Select } from '@/shared/ui/primitives';
import { translationsApi, type ImportEntry, type ImportResult } from '../api/translations.api';
import { useImportTranslations } from '../hooks/useTranslations';

interface Props {
  open: boolean;
  namespaces: string[];
  onClose: () => void;
}

/**
 * Combined import + export modal. Round-trips through the same JSON
 * shape the extractor produces (`docs/i18n/seed/translations.seed.json`).
 */
export default function ImportExportPanel({ open, namespaces, onClose }: Props) {
  const { t } = useTranslation();
  const [exportNs, setExportNs] = useState<string | undefined>(undefined);
  const [exportBusy, setExportBusy] = useState(false);
  const [importMode, setImportMode] = useState<'SKIP_EXISTING' | 'OVERWRITE'>('SKIP_EXISTING');
  const [overwriteConfirm, setOverwriteConfirm] = useState('');
  const [lastResult, setLastResult] = useState<ImportResult | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const importMut = useImportTranslations();
  const overwriteUnlocked = overwriteConfirm.trim() === 'OVERWRITE';

  const handleExport = async () => {
    setExportBusy(true);
    try {
      const data = await translationsApi.export(exportNs);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportNs
        ? `translations-${exportNs}-${dateStamp()}.json`
        : `translations-all-${dateStamp()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('translations.toast.exported', { count: data.length }));
    } catch {
      toast.error(t('translations.toast.exportFailed'));
    } finally {
      setExportBusy(false);
    }
  };

  const handleImport = async (file: File) => {
    if (importMode === 'OVERWRITE' && !overwriteUnlocked) {
      toast.error(t('translations.toast.overwriteFirst'));
      return;
    }
    let parsed: ImportEntry[];
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      if (!Array.isArray(json)) throw new Error('expected an array of entries');
      parsed = json;
    } catch (e: any) {
      toast.error(t('translations.toast.invalidJson'));
      return;
    }
    if (parsed.length === 0) {
      toast.error(t('translations.toast.emptyFile'));
      return;
    }
    if (importMode === 'OVERWRITE') {
      const sample = parsed.slice(0, 3).map((e) => e.key).join(', ');
      const confirmed = window.confirm(
        t('translations.import.confirmOverwrite', { count: parsed.length, sample }),
      );
      if (!confirmed) return;
    }
    importMut.mutate(
      { mode: importMode, entries: parsed },
      {
        onSuccess: (res) => {
          setLastResult(res);
          setOverwriteConfirm('');
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      title={t('translations.import.title')}
      onCancel={onClose}
      footer={<Button onClick={onClose}>{t('common.close', { defaultValue: 'Close' })}</Button>}
    >
      <div style={{ display: 'grid', gap: 20 }}>
        <section>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('translations.import.exportTitle')}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Select
              value={exportNs ?? null}
              onChange={(v) => setExportNs(v ? String(v) : undefined)}
              allowClear
              placeholder={t('translations.import.allModules')}
              options={namespaces.map((n) => ({ value: n, label: moduleLabel(n, t) }))}
              style={{ width: 240 }}
            />
            <Button onClick={handleExport} loading={exportBusy} type="primary">
              {t('translations.import.download')}
            </Button>
          </div>
        </section>

        <section>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('translations.import.importTitle')}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Select
              value={importMode}
              onChange={(v) => {
                setImportMode(v as 'SKIP_EXISTING' | 'OVERWRITE');
                setOverwriteConfirm('');
              }}
              options={[
                { value: 'SKIP_EXISTING', label: t('translations.import.skipExisting') },
                { value: 'OVERWRITE', label: t('translations.import.overwriteExisting') },
              ]}
              style={{ width: 280 }}
            />
            <input
              type="file"
              accept="application/json"
              ref={fileRef}
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleImport(f);
                e.target.value = '';
              }}
            />
            <Button
              onClick={() => fileRef.current?.click()}
              loading={importMut.isPending}
              disabled={importMode === 'OVERWRITE' && !overwriteUnlocked}
              danger={importMode === 'OVERWRITE'}
            >
              {t('translations.import.chooseFile')}
            </Button>
          </div>

          {importMode === 'OVERWRITE' && (
            <div
              style={{
                marginTop: 10,
                padding: 10,
                border: '1px solid #ffccc7',
                background: '#fff2f0',
                borderRadius: 4,
                fontSize: 13,
              }}
            >
              <div style={{ fontWeight: 600, color: '#a8071a', marginBottom: 6 }}>
                {t('translations.import.destructiveTitle')}
              </div>
              <div style={{ marginBottom: 8, color: '#a8071a' }}>
                {t('translations.import.destructiveBody')}{' '}
                {t('translations.import.typeWord')} <code style={{ background: '#fff', padding: '0 4px' }}>OVERWRITE</code>{' '}
                {t('translations.import.destructiveSuffix')}
              </div>
              <Input
                value={overwriteConfirm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setOverwriteConfirm(e.target.value)
                }
                placeholder={t('translations.import.overwritePlaceholder')}
                style={{ maxWidth: 320 }}
              />
            </div>
          )}

          <div style={{ color: '#666', fontSize: 12, marginTop: 6 }}>
            {t('translations.import.shapeHint')}
          </div>
        </section>

        {lastResult && (
          <section
            style={{
              border: '1px solid #e6e6e6',
              borderRadius: 4,
              padding: 12,
              background: '#fafafa',
              fontSize: 13,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{t('translations.import.lastImport')}</div>
            <div>{t('translations.import.mode')}: {modeLabel(lastResult.mode, t)}</div>
            <div>{t('translations.import.entries')}: {lastResult.entries}</div>
            <div>{t('translations.import.created')}: {lastResult.keysCreated}</div>
            <div>{t('translations.import.existing')}: {lastResult.keysExisted}</div>
            <div>{t('translations.import.inserted')}: {lastResult.valuesInserted}</div>
            <div>{t('translations.import.updated')}: {lastResult.valuesUpdated}</div>
            <div>{t('translations.import.skipped')}: {lastResult.valuesSkipped}</div>
            {lastResult.badEntries > 0 && (
              <div style={{ color: '#d4380d' }}>{t('translations.import.badEntries')}: {lastResult.badEntries}</div>
            )}
            {lastResult.errors.length > 0 && (
              <details style={{ marginTop: 6 }}>
                <summary>{t('translations.import.errors', { count: lastResult.errors.length })}</summary>
                <pre style={{ maxHeight: 160, overflow: 'auto', fontSize: 11 }}>
                  {JSON.stringify(lastResult.errors, null, 2)}
                </pre>
              </details>
            )}
          </section>
        )}
      </div>
    </Modal>
  );
}

function moduleLabel(namespace: string, t: (key: string, options?: any) => string): string {
  const translated = t(`translations.modules.${String(namespace).toLowerCase()}`, { defaultValue: '' });
  if (translated) return translated;
  return namespace
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function modeLabel(mode: string, t: (key: string, options?: any) => string): string {
  return mode === 'OVERWRITE' ? t('translations.import.overwriteExisting') : t('translations.import.skipExisting');
}

function dateStamp() {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}
